import { useState, useEffect } from 'react';
import {
    Box, Button, Dialog, DialogTitle, DialogContent,
    DialogActions, Typography, CircularProgress
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Order } from '../../../app/models/order';
import { Question } from '../../../app/models/question';
import agent from '../../../app/api/agent';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../../../app/store/configureStore';
import { fetchAdminOrderAsync } from './adminOrdersSlice';
import AppQuestionEditor from '../../../app/components/AppQuestionEditor';

interface AdminTestQuestionsEditorProps {
    order: Order;
    disabled?: boolean;
}

export default function AdminTestQuestionsEditor({ order, disabled = false }: AdminTestQuestionsEditorProps) {
    const [open, setOpen] = useState(false);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const dispatch = useAppDispatch();

    // Load and parse questions when dialog opens
    useEffect(() => {
        if (open && order) {
            setLoading(true);
            try {
                let parsedQuestions: Question[] = [];

                // Parse questionsJson to get Question[] array
                if (order.questionsJson) {
                    if (typeof order.questionsJson === 'string') {
                        try {
                            parsedQuestions = JSON.parse(order.questionsJson);
                        } catch (e) {
                            console.error("Błąd parsowania questionsJson:", e);
                        }
                    } else if (Array.isArray(order.questionsJson)) {
                        parsedQuestions = order.questionsJson as Question[];
                    } else if (typeof order.questionsJson === 'object') {
                        parsedQuestions = [order.questionsJson] as Question[];
                    }
                } else {
                    parsedQuestions = [];
                }

                // Jeśli nie ma pytań, dodaj jedno puste pytanie
                if (parsedQuestions.length === 0) {
                    parsedQuestions = [{
                        id: "1",
                        text: "",
                        options: ["", ""],
                        correctOptionIndex: 0
                    }];
                }

                // Upewnij się, że każde pytanie ma wszystkie wymagane pola
                parsedQuestions = parsedQuestions.map((q, index) => ({
                    id: q.id || (index + 1).toString(),
                    text: q.text || '',
                    options: Array.isArray(q.options) && q.options.length > 0
                        ? q.options
                        : ["", ""],
                    correctOptionIndex: typeof q.correctOptionIndex === 'number' ? q.correctOptionIndex : 0
                }));

                setQuestions(parsedQuestions);
            } catch (error) {
                console.error("Błąd podczas ładowania pytań:", error);
                toast.error("Nie udało się załadować pytań z formularza");
                // W przypadku błędu - ustaw domyślne puste pytanie
                setQuestions([{
                    id: "1",
                    text: "",
                    options: ["", ""],
                    correctOptionIndex: 0
                }]);
            } finally {
                setLoading(false);
            }
        }
    }, [open, order]);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSaveAllQuestions = async () => {
        // Walidacja pytań przed zapisem
        const validQuestions = questions
            .map((q) => ({
                ...q,
                text: q.text.trim(),
                options: q.options.map((option) => option.trim()).filter((option) => option !== "")
            }))
            .filter((q) => q.text !== "" && q.options.length >= 2);

        if (validQuestions.length === 0) {
            toast.error("Musisz podać przynajmniej jedno pytanie z minimum dwiema odpowiedziami, a żadne pole nie może być puste");
            return;
        }

        setSaving(true);

        try {
            // Send the updated questions to the server (zachowujemy oryginalne id)
            await agent.Admin.Orders.updateQuestionsJson(
                order.publicId,
                { questionsJson: validQuestions }
            );

            // Refresh order data
            dispatch(fetchAdminOrderAsync(order.publicId));

            toast.success("Pytania testowe zostały zaktualizowane");
            handleClose();
        } catch (error) {
            console.error("Błąd podczas zapisywania pytań:", error);
            toast.error("Nie udało się zapisać pytań testowych");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleOpen}
                variant="contained"
                color="primary"
                disabled={disabled}
                sx={{ width: '100%', minHeight: 56, whiteSpace: 'nowrap' }}
            >
                Edytuj pytania
            </Button>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>
                    <Typography variant="subtitle2" color="textSecondary">
                        Test #{order.publicId}: {order.customeClientServiceName || order.serviceName}
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    {loading ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <AppQuestionEditor
                            questions={questions}
                            onQuestionsChange={setQuestions}
                        />
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} color="inherit">
                        Anuluj
                    </Button>
                    <LoadingButton
                        onClick={handleSaveAllQuestions}
                        color="success"
                        variant="contained"
                        loading={saving}
                    >
                        Zapisz
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
}
